import { ALERT_STATUS } from "~/constants/alert";
import { toAlert, toAlertList } from "~/transformers/alertTransformer";

export default {
  async fetch({ commit, dispatch }, params = {}) {
    commit("SET_LOADING", true);
    try {
      await Promise.all([dispatch("fetchEvents", params), dispatch("fetchDqlFields")]);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchInitialForAgency({ commit, dispatch }) {
    commit("SET_LOADING", true);
    try {
      await Promise.all([dispatch("fetchItems"), dispatch("fetchDqlFields")]);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchEvents({ commit, rootGetters, state }, params = {}) {
    const size = rootGetters["application/getPageSize"];
    const { page } = state.paginator;
    const { field: sortBy, order: orderBy } = state.sort;
    const { filter, defaultFilter, type, dateFrom, dateTo } = params;
    const queryParams = {
      size,
      page,
      sortBy,
      orderBy,
      filter,
      defaultFilter,
      type,
      dateFrom,
      dateTo,
    };
    try {
      const response = await this.$services.alert.fetch(queryParams);
      commit("SET_ITEMS", toAlertList(response.collection));
      commit("SET_PAGINATOR", { page: response.page, size: response.size, total: response.total });
      commit("SET_PAGINATOR_LOADING_PAGE", null);
      commit("SET_SORT_LOADING", false);
    } catch (error) {
      throw new Error(error);
    }
  },

  async fetchDqlFields({ commit }) {
    try {
      const fields = await this.$services.alert.fetchDqlFields();
      commit("SET_DQL_FIELDS", fields);
    } catch {
      commit("SET_DQL_FIELDS", {});
    }
  },

  async searchDql({ commit, rootGetters, state }, params = {}) {
    const size = rootGetters["application/getPageSize"];
    const { page } = state.paginator;
    const { field: sortBy, order: orderBy } = state.sort;
    const { body, dateFrom, dateTo, type, defaultFilter } = params;
    const queryParams = { size, page, sortBy, orderBy, dateFrom, dateTo, type, defaultFilter };
    try {
      const response = await this.$services.alert.search(queryParams, body);
      commit("SET_ITEMS", toAlertList(response.collection));
      commit("SET_PAGINATOR", { page: response.page, size: response.size, total: response.total });
      commit("SET_PAGINATOR_LOADING_PAGE", null);
      commit("SET_SORT_LOADING", false);
    } catch (error) {
      throw new Error(error);
    }
  },

  async searchDqlForAgency({ commit, rootGetters, state }, params = {}) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const size = rootGetters["application/getPageSize"];
    const { page } = state.paginator;
    const { field: sortBy, order: orderBy } = state.sort;
    const { body, dateFrom, dateTo, type, defaultFilter } = params;
    const queryParams = { size, page, sortBy, orderBy, dateFrom, dateTo, type, defaultFilter };
    try {
      const response = agencyId
        ? await this.$services.alert.searchByAgency(agencyId, queryParams, body)
        : await this.$services.alert.search(queryParams, body);
      commit("SET_ITEMS", toAlertList(response.collection));
      commit("SET_PAGINATOR", { page: response.page, size: response.size, total: response.total });
      commit("SET_PAGINATOR_LOADING_PAGE", null);
      commit("SET_SORT_LOADING", false);
    } catch (error) {
      throw new Error(error);
    }
  },

  async fetchItems({ commit, rootGetters, state }, params = {}) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const size = rootGetters["application/getPageSize"];
    const { page } = state.paginator;
    const { field: sortBy, order: orderBy } = state.sort;
    const { filter, defaultFilter, type, dateFrom, dateTo } = params;
    const queryParams = {
      size,
      page,
      sortBy,
      orderBy,
      filter,
      defaultFilter,
      type,
      dateFrom,
      dateTo,
    };
    try {
      const response = agencyId
        ? await this.$services.alert.fetchByAgency(agencyId, queryParams)
        : await this.$services.alert.fetch(queryParams);
      commit("SET_ITEMS", toAlertList(response.collection));
      commit("SET_PAGINATOR", { page: response.page, size: response.size, total: response.total });
      commit("SET_PAGINATOR_LOADING_PAGE", null);
      commit("SET_SORT_LOADING", false);
    } catch (error) {
      throw new Error(error);
    }
  },

  updatePage({ commit }, page) {
    commit("SET_PAGINATOR_LOADING_PAGE", page);
    commit("SET_PAGINATOR_PAGE", page);
  },

  changeSort({ commit }, { field, order }) {
    commit("SET_SORT", { field, order });
    commit("SET_SORT_LOADING", true);
  },

  async changeSizeTable({ commit, dispatch }, { size }) {
    await dispatch("application/savePageSize", size, { root: true });
    commit("SET_PAGINATOR_PAGE", 0);
    commit("SET_PAGINATOR_LOADING_PAGE", 0);
  },

  resetPagination({ commit }) {
    commit("SET_PAGINATOR_PAGE", 0);
    commit("SET_SORT", { field: "createdOn", order: "desc" });
  },

  async patchAlert({ commit }, { id, status }) {
    try {
      const updated = await this.$services.alert.patch(id, { status });
      commit("UPDATE_ITEM", toAlert(updated));
      this.$feedback.ok("feedback.update.alert.ok");
    } catch {
      this.$feedback.error("feedback.update.alert.error");
    }
  },

  async resolveSelected({ commit }, selection) {
    try {
      await this.$services.alert.bulkUpdate({ alerts: selection, status: ALERT_STATUS.RESOLVED });
      commit("BULK_UPDATE_STATUS", { ids: selection, status: ALERT_STATUS.RESOLVED });
      this.$feedback.ok("feedback.resolve.alerts.bulk.ok");
    } catch {
      this.$feedback.error("feedback.resolve.alerts.bulk.error");
    }
  },

  async rejectSelected({ commit }, selection) {
    try {
      await this.$services.alert.bulkUpdate({ alerts: selection, status: ALERT_STATUS.REJECTED });
      commit("BULK_UPDATE_STATUS", { ids: selection, status: ALERT_STATUS.REJECTED });
      this.$feedback.ok("feedback.reject.alerts.bulk.ok");
    } catch {
      this.$feedback.error("feedback.reject.alerts.bulk.error");
    }
  },

  setCurrent({ commit, state }, id) {
    const alert = state.items.find((item) => item.id === id) ?? null;
    commit("SET_CURRENT", alert);
  },

  async prepareCsv({ rootGetters, commit, dispatch }, params) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    try {
      if (agencyId) {
        await this.$services.alert.preparecsvByAgency(agencyId, params);
      } else {
        await this.$services.alert.preparecsv(params);
      }
      await dispatch("application/setDownloads", {}, { root: true });
      commit("application/SET_IS_WAITING_FOR_DOWNLOADS", true, { root: true });
      this.$feedback.ok("generic.download.ok");
    } catch {
      this.$feedback.error("generic.download.error");
    }
  },
};
